import GenericLoading from '@cannect/components/GenericLoading'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import api from '../../../../../../services/api'
import { useCheckout } from '../hooks/useCheckout'
import { useOrderCart } from '../hooks/useOrderCart'
import {
  AddressContainer,
  AddressForm,
  AddressInput,
  AddressLabel,
  CartContainer,
  CartHeader,
  CartTitle,
  CheckoutButton,
  ErrorContainer,
  FormsGrid
} from '../styles'

interface ViaCepResponse {
  cep: string
  logradouro: string
  complemento: string
  bairro: string
  localidade: string
  uf: string
  erro?: boolean
}

interface CheckoutProps {
  onCheckoutComplete?: () => void
}

interface AddressFormData {
  zipcode: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  is_discrete_shipping?: boolean
}

interface CustomerFormData {
  email: string
  name: string
  cpf: string
  birth_date: string
  phone: string
}

interface AddressResponse {
  id: number
  address: string
  cep: string
  district: string
  city: string
  complement: string
  number: string
  state: string
  type: string
}

export default function Checkout({ onCheckoutComplete }: CheckoutProps) {
  const { order, loading: orderLoading, fetchOrder } = useOrderCart()
  const { loading: checkoutLoading, submitCheckout } = useCheckout()
  const [addressData, setAddressData] = useState<AddressFormData>({
    zipcode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    is_discrete_shipping: false
  })
  const [customerData, setCustomerData] = useState<CustomerFormData>({
    name: '',
    cpf: '',
    birth_date: '',
    phone: '',
    email: ''
  })
  const [loadingCep, setLoadingCep] = useState(false)

  const fetchAddressByCep = useCallback(async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '')

    if (cleanCep.length !== 8) return

    setLoadingCep(true)
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
      const data: ViaCepResponse = await response.json()

      if (data.erro) {
        toast.error('CEP não encontrado')
        return
      }

      setAddressData((prev) => ({
        ...prev,
        street: data.logradouro || prev.street,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: data.uf || prev.state
      }))
    } catch (err) {
      console.error('Error fetching address by CEP:', err)
      toast.error('Erro ao buscar endereço pelo CEP')
    } finally {
      setLoadingCep(false)
    }
  }, [])

  const fetchUserData = useCallback(async () => {
    try {
      const response = await api.get('/me?association_id=1')
      if (response.data?.success && response.data.user) {
        const { user } = response.data
        const { person } = user

        if (person) {
          setCustomerData({
            name: person.name || user.name || '',
            cpf: person.cpf || user.cpf || '',
            birth_date: person.birthday || '',
            phone: person.phone || user.phone || '',
            email: person.email || user.email || ''
          })

          const personalAddress = person.addresses?.find((addr: AddressResponse) => addr.type === 'PERSONAL')

          if (personalAddress) {
            setAddressData({
              zipcode: personalAddress.cep || '',
              street: personalAddress.address || '',
              number: personalAddress.number || '',
              complement: personalAddress.complement || '',
              neighborhood: personalAddress.district || '',
              city: personalAddress.city || '',
              state: personalAddress.state || ''
            })
          }
        } else {
          setCustomerData({
            name: user.name || '',
            cpf: user.cpf || '',
            birth_date: '',
            phone: user.phone || '',
            email: user.email || ''
          })
        }
      }
    } catch (err) {
      console.error('Error fetching user data:', err)
    }
  }, [])

  useEffect(() => {
    fetchOrder()
    fetchUserData()
  }, [fetchOrder, fetchUserData])

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setAddressData((prev) => ({
      ...prev,
      [name]: value
    }))

    if (name === 'zipcode') {
      const cleanCep = value.replace(/\D/g, '')
      if (cleanCep.length === 8) {
        fetchAddressByCep(cleanCep)
      }
    }
  }

  const handleCustomerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setCustomerData((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const validateShipping = async (): Promise<void> => {
    const items = order!.items.map((item) => ({
      product_id: item.product_id,
      price: item.product.price,
      quantity: item.quantity
    }))

    await api.post('/shipping/calculate', {
      postal_code: addressData.zipcode,
      items
    })
  }

  const handleSubmitCheckout = async () => {
    if (!order) {
      toast.error('Erro ao carregar dados do pedido')
      return
    }

    if (
      !addressData.zipcode ||
      !addressData.street ||
      !addressData.number ||
      !addressData.neighborhood ||
      !addressData.city ||
      !addressData.state
    ) {
      toast.error('Por favor, preencha todos os campos de endereço obrigatórios')
      return
    }

    if (!customerData.name || !customerData.cpf || !customerData.birth_date || !customerData.phone) {
      toast.error('Por favor, preencha todos os campos do paciente')
      return
    }

    try {
      await validateShipping()
    } catch (err: any) {
      const field = err?.response?.data?.field
      if (field === 'postal_code') {
        toast.error('CEP inválido. Verifique o CEP informado e tente novamente.')
      } else {
        toast.error('Erro ao validar o CEP para entrega. Tente novamente.')
      }
      return
    }

    try {
      await submitCheckout(order.id, addressData, order.items, customerData)
      toast.success('Pedido atualizado com sucesso!')
      onCheckoutComplete?.()
    } catch (err) {
      toast.error('Erro ao atualizar pedido')
    }
  }

  if (orderLoading) {
    return <GenericLoading loading />
  }

  if (!order) {
    return <ErrorContainer>Nenhum pedido encontrado</ErrorContainer>
  }

  return (
    <CartContainer>
      <CartHeader>
        <CartTitle>Finalizar Compra</CartTitle>
      </CartHeader>

      <FormsGrid>
        <AddressContainer>
          <h2>Endereço de Entrega</h2>
          <AddressForm>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="is_discrete_shipping"
                checked={!!addressData.is_discrete_shipping}
                onChange={(e) =>
                  setAddressData((prev) => ({
                    ...prev,
                    is_discrete_shipping: e.target.checked
                  }))
                }
              />
              <AddressLabel htmlFor="is_discrete_shipping" style={{ marginBottom: 0 }}>
                Entrega discreta (sem identificação da associação)
              </AddressLabel>
            </div>
            <div style={{ position: 'relative' }}>
              <AddressLabel htmlFor="zipcode">CEP *</AddressLabel>
              <AddressInput
                type="text"
                id="zipcode"
                name="zipcode"
                value={addressData.zipcode}
                onChange={handleAddressChange}
                placeholder="00000-000"
                maxLength={9}
                required
              />
              {loadingCep && (
                <span
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '38px',
                    fontSize: '0.75rem',
                    color: '#02b0bb'
                  }}>
                  Buscando...
                </span>
              )}
            </div>

            <div>
              <AddressLabel htmlFor="street">Rua *</AddressLabel>
              <AddressInput
                type="text"
                id="street"
                name="street"
                value={addressData.street}
                onChange={handleAddressChange}
                placeholder="Nome da rua"
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
              <div>
                <AddressLabel htmlFor="number">Número *</AddressLabel>
                <AddressInput
                  type="text"
                  id="number"
                  name="number"
                  value={addressData.number}
                  onChange={handleAddressChange}
                  placeholder="123"
                  required
                />
              </div>

              <div>
                <AddressLabel htmlFor="complement">Complemento</AddressLabel>
                <AddressInput
                  type="text"
                  id="complement"
                  name="complement"
                  value={addressData.complement}
                  onChange={handleAddressChange}
                  placeholder="Apto"
                />
              </div>
            </div>

            <div>
              <AddressLabel htmlFor="neighborhood">Bairro *</AddressLabel>
              <AddressInput
                type="text"
                id="neighborhood"
                name="neighborhood"
                value={addressData.neighborhood}
                onChange={handleAddressChange}
                placeholder="Nome do bairro"
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
              <div>
                <AddressLabel htmlFor="city">Cidade *</AddressLabel>
                <AddressInput
                  type="text"
                  id="city"
                  name="city"
                  value={addressData.city}
                  onChange={handleAddressChange}
                  placeholder="Cidade"
                  required
                />
              </div>

              <div>
                <AddressLabel htmlFor="state">Estado *</AddressLabel>
                <AddressInput
                  type="text"
                  id="state"
                  name="state"
                  value={addressData.state}
                  onChange={handleAddressChange}
                  placeholder="UF"
                  maxLength={2}
                  required
                />
              </div>
            </div>
          </AddressForm>
        </AddressContainer>

        <AddressContainer>
          <h2>Dados do Paciente</h2>
          <AddressForm>
            <div>
              <AddressLabel htmlFor="name">Nome Completo *</AddressLabel>
              <AddressInput
                type="text"
                id="name"
                name="name"
                value={customerData.name}
                onChange={handleCustomerChange}
                placeholder="Nome completo"
                required
              />
            </div>

            <div>
              <AddressLabel htmlFor="cpf">CPF *</AddressLabel>
              <AddressInput
                type="text"
                id="cpf"
                name="cpf"
                value={customerData.cpf}
                onChange={handleCustomerChange}
                placeholder="000.000.000-00"
                required
              />
            </div>

            <div>
              <AddressLabel htmlFor="birth_date">Data de Nascimento *</AddressLabel>
              <AddressInput
                type="date"
                id="birth_date"
                name="birth_date"
                value={customerData.birth_date}
                onChange={handleCustomerChange}
                required
              />
            </div>

            <div>
              <AddressLabel htmlFor="phone">Telefone *</AddressLabel>
              <AddressInput
                type="text"
                id="phone"
                name="phone"
                value={customerData.phone}
                onChange={handleCustomerChange}
                placeholder="(00) 00000-0000"
                required
              />
            </div>

            <div>
              <AddressLabel htmlFor="email">Email *</AddressLabel>
              <AddressInput
                type="email"
                id="email"
                name="email"
                value={customerData.email}
                onChange={handleCustomerChange}
                placeholder="exemplo@email.com"
                required
              />
            </div>
          </AddressForm>
        </AddressContainer>
      </FormsGrid>

      <CheckoutButton onClick={handleSubmitCheckout} disabled={checkoutLoading}>
        {checkoutLoading ? 'Processando...' : 'Continuar'}
      </CheckoutButton>
    </CartContainer>
  )
}
